import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import type { Federation, UpdateFederationDTO } from '@/services/federation.service';
import FederationService from '@/services/federation.service';
import { logger } from '@/utils/logger';
import { AppError, ErrorCodes } from '@/utils/errors';

// Добавляем функцию валидации
const isLatinOrCyrillic = (str: string) => {
    // Regex для проверки на латиницу, кириллицу, пробелы, дефисы и кавычки
    return /^[a-zA-Zа-яА-ЯёЁ\s\-'"]+$/.test(str) && str.trim().length >= 2;
};

interface EditFederationModalProps {
    visible: boolean;
    federation: Federation | null;
    onCancel: () => void;
    onSuccess: () => void;
}

export const EditFederationModal: React.FC<EditFederationModalProps> = ({
    visible,
    federation,
    onCancel,
    onSuccess,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = React.useState(false);

    useEffect(() => {
        if (visible && federation) {
            form.setFieldsValue({
                name: federation.name,
                country: federation.country,
                region: federation.region,
                city: federation.city,  // Добавляем город
                president: federation.president,
                description: federation.description,
                phone: federation.phone,
                email: federation.email,
                website: federation.website,
                address: federation.address,
                status: federation.status
            });
        }
    }, [visible, federation, form]);

    const handleSubmit = async (values: UpdateFederationDTO) => {
        if (!federation) {
            message.error('Федерация не найдена');
            onCancel();
            return;
        }

        setLoading(true);

        try {
            // Токены теперь в httpOnly cookies, они отправляются автоматически

            // Очищаем и валидируем данные перед отправкой
            const federationData: UpdateFederationDTO = {
                name: values.name?.trim() || '',
                country: values.country?.trim() || '',
                region: values.region?.trim(),
                city: values.city?.trim(),  // Добавляем город
                president: values.president?.trim(),
                description: values.description?.trim(),
                phone: values.phone?.trim(),
                email: values.email?.trim(),
                website: values.website?.trim(),
                address: values.address?.trim(),
                status: values.status
            };

            // Проверяем обязательные поля
            if (!federationData.name || !federationData.country) {
                message.error('Название федерации и страна обязательны для заполнения');
                return;
            }

            try {
                await FederationService.updateFederation(federation.id, federationData);
                message.success('Федерация успешно обновлена');
                form.resetFields();
                onSuccess();
                onCancel();
            } catch (updateError) {
                // Если ошибка с кодом INTERNAL_ERROR и статусом 200, считаем это успехом
                if (updateError instanceof AppError &&
                    updateError.code === ErrorCodes.INTERNAL_ERROR &&
                    updateError.statusCode === 200) {
                    logger.warn('Ignoring INTERNAL_ERROR with status 200, treating as success');
                    message.success('Федерация успешно обновлена');
                    form.resetFields();
                    onSuccess();
                    onCancel();
                    return;
                }
                throw updateError; // Перебрасываем ошибку дальше для обработки в блоке catch
            }
        } catch (error: any) {
            logger.error('Error updating federation', {
                error,
                errorMessage: error.response?.data?.message || error.message,
                formData: values,
                federationId: federation.id
            });

            if (error instanceof AppError) {
                switch (error.statusCode) {
                    case 401:
                        message.error('Необходима авторизация');
                        // Токены в httpOnly cookies, редирект обрабатывается в AuthContext
                        break;
                    case 404:
                        message.error('Федерация не найдена. Возможно, она была удалена.');
                        onCancel();
                        break;
                    case 409:
                        message.error('Федерация с таким названием уже существует');
                        break;
                    default:
                        message.error(error.message || 'Произошла ошибка при обновлении федерации');
                }
            } else {
                message.error('Произошла ошибка при обновлении федерации');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Редактирование федерации"
            open={visible}
            onCancel={onCancel}
            onOk={form.submit}
            confirmLoading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{ required: true, message: 'Введите название федерации' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="country"
                    label="Страна"
                    rules={[{ required: true, message: 'Введите страну' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="region"
                    label="Регион"
                    rules={[
                        { max: 100, message: 'Максимум 100 символов' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="city"
                    label="Город"
                    rules={[
                        { max: 100, message: 'Максимум 100 символов' },
                        {
                            validator: async (_, value) => {
                                if (!value) return;

                                if (!isLatinOrCyrillic(value)) {
                                    throw new Error('Используйте только буквы, пробелы и дефис');
                                }

                                if (value.trim().length < 2) {
                                    throw new Error('Минимум 2 символа без учета пробелов');
                                }
                            }
                        }
                    ]}
                >
                    <Input placeholder="Например: Москва" />
                </Form.Item>

                <Form.Item
                    name="president"
                    label="Президент"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                >
                    <Input.TextArea />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label="Телефон"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: 'email', message: 'Введите корректный email' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="website"
                    label="Веб-сайт"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Адрес"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Статус"
                >
                    <Select>
                        <Select.Option value="active">Активна</Select.Option>
                        <Select.Option value="inactive">Неактивна</Select.Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};